import { createBrowserRouter } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import Layout from './components/layout/Layout';
import AdminLayout from './components/admin/AdminLayout';
import ProtectedRoute from './components/admin/ProtectedRoute';
import MaintenanceWrapper from './components/MaintenanceWrapper';
import LoadingSpinner from './components/shared/LoadingSpinner';

// Route Types
export type RouteParams = {
  slug?: string;
};

// Lazy Components
const Home = lazy(() => import('./pages/Home'));
const About = lazy(() => import('./pages/About'));
const Team = lazy(() => import('./pages/Team'));
const TeamMember = lazy(() => import('./pages/TeamMember'));
const PracticeAreas = lazy(() => import('./pages/PracticeAreas'));
const PracticeAreaDetail = lazy(() => import('./pages/PracticeAreaDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const Career = lazy(() => import('./pages/Career'));
const Login = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
const AdminPracticeAreas = lazy(() => import('./pages/admin/AdminPracticeAreas'));
const AdminCareers = lazy(() => import('./pages/admin/AdminCareers'));
const AdminMessages = lazy(() => import('./pages/admin/AdminMessages'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const Maintenance = lazy(() => import('./pages/Maintenance'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading Component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <LoadingSpinner />
  </div>
);

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <MaintenanceWrapper><Layout /></MaintenanceWrapper>,
      children: [
        { 
          index: true, 
          element: <Suspense fallback={<PageLoader />}><Home /></Suspense> 
        },
        { 
          path: 'hakkimizda', 
          element: <Suspense fallback={<PageLoader />}><About /></Suspense> 
        },
        { 
          path: 'ekibimiz', 
          element: <Suspense fallback={<PageLoader />}><Team /></Suspense> 
        },
        { 
          path: 'ekibimiz/:slug', 
          element: <Suspense fallback={<PageLoader />}><TeamMember /></Suspense> 
        },
        { 
          path: 'faaliyet-alanlari', 
          element: <Suspense fallback={<PageLoader />}><PracticeAreas /></Suspense> 
        },
        { 
          path: 'faaliyet-alanlari/:slug', 
          element: <Suspense fallback={<PageLoader />}><PracticeAreaDetail /></Suspense> 
        },
        { 
          path: 'kariyer', 
          element: <Suspense fallback={<PageLoader />}><Career /></Suspense> 
        },
        { 
          path: 'iletisim', 
          element: <Suspense fallback={<PageLoader />}><Contact /></Suspense> 
        },
      ],
      errorElement: <Suspense fallback={<PageLoader />}><NotFound /></Suspense>,
    },
    {
      path: '/bakim',
      element: <Suspense fallback={<PageLoader />}><Maintenance /></Suspense>,
      errorElement: <Suspense fallback={<PageLoader />}><NotFound /></Suspense>,
    },
    {
      path: '/yonetim/giris',
      element: <Suspense fallback={<PageLoader />}><Login /></Suspense>,
      errorElement: <Suspense fallback={<PageLoader />}><NotFound /></Suspense>,
    },
    {
      path: '/yonetim',
      element: <ProtectedRoute element={<AdminLayout />} />,
      children: [
        { 
          index: true, 
          element: <Suspense fallback={<PageLoader />}><AdminDashboard /></Suspense> 
        },
        { 
          path: 'ekip-uyeleri', 
          element: <Suspense fallback={<PageLoader />}><AdminTeam /></Suspense> 
        },
        { 
          path: 'faaliyet-alanlari', 
          element: <Suspense fallback={<PageLoader />}><AdminPracticeAreas /></Suspense> 
        },
        { 
          path: 'kariyer', 
          element: <Suspense fallback={<PageLoader />}><AdminCareers /></Suspense> 
        },
        { 
          path: 'iletisim', 
          element: <Suspense fallback={<PageLoader />}><AdminMessages /></Suspense> 
        },
        { 
          path: 'ayarlar', 
          element: <Suspense fallback={<PageLoader />}><AdminSettings /></Suspense> 
        },
      ],
      errorElement: <Suspense fallback={<PageLoader />}><NotFound /></Suspense>,
    },
    {
      path: '*',
      element: <Suspense fallback={<PageLoader />}><NotFound /></Suspense>,
    },
  ],
  {
    future: {
      v7_startTransition: true,
    },
  }
);
